// Pure "show the math" helpers for the Sales Order pages (SalesOrderPage +
// SalesOrderEntryPage). Everything here is derived from fields that are on the
// screen, so an operator can reproduce every dollar: ext = qty × unit,
// margin = (price − cost) / price, header total = lines + shipping + fees + tax
// − discount. The enriched /truth and /production_summary keys (unit_cost,
// price_source, quote_part_id, sets, per_set_price, order_subtotal,
// quote_cost_basis, estimate_splits, not_yet_realizable_lines …) are consumed
// when present and every helper degrades to "unknown" (null) rather than NaN or
// a silent $0.00 when they are absent (older API, older rows).

import { num, marginPct, approxEq, fmtMoney, priceSourceLabel } from './priceMath';

/** Number or null — never 0 for "missing". */
export function numOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Badge class + tooltip per sales_order_lines.price_source. Label comes from priceMath.priceSourceLabel. */
export const PRICE_SOURCE_META = {
  quote_line: {
    cls: 'badge bg-primary',
    title: 'Unit price copied from the quote line at conversion.',
  },
  ops_fold: {
    cls: 'badge bg-info text-dark',
    title: "The quote's assembly-operations line was folded into this primary at conversion: Σ(ops price × ops qty) ÷ primary qty is included in the unit price.",
  },
  assembly_override: {
    cls: 'badge bg-warning text-dark',
    title: 'Assembly override price ÷ primary-parts-per-assembly applied to the primary line; member lines carry $0.00 by design.',
  },
  override_member: {
    cls: 'badge bg-warning text-dark',
    title: 'Priced by the assembly override carried on the primary line — $0.00 on this line by design.',
  },
  manual: {
    cls: 'badge bg-secondary',
    title: 'Unit price typed by an operator when the line was added.',
  },
  catalog: {
    cls: 'badge bg-light text-dark border',
    title: 'Priced from the part\'s book price (parts.price — set on quote approval or on the Parts page).',
  },
  // Time & Materials: the line has no price until Approve T&M Pricing runs on the order.
  tm_pending: {
    cls: 'badge bg-info text-dark',
    title: 'Time & Materials: priced at completion (Approve T&M Pricing on this order). $0.00 until then.',
  },
  tm_manual: {
    cls: 'badge bg-success',
    title: 'Time & Materials: unit price typed at Approve T&M Pricing.',
  },
  tm_actuals: {
    cls: 'badge bg-success',
    title: "Time & Materials: unit price from this order's own shop time and material (Approve T&M Pricing).",
  },
};

export function priceSourceMeta(source) {
  if (!source) return null;
  const meta = PRICE_SOURCE_META[source] || { cls: 'badge bg-light text-dark border', title: `price_source = ${source}` };
  return { source, label: priceSourceLabel(source), ...meta };
}

/**
 * Per-line money. Prefers the enriched /truth fields, recomputes the rest.
 *   ext_price = quantity_ordered × unit_price
 *   unit_cost = unit_cost ?? unit_cost_snapshot (null when the line has no snapshot)
 *   ext_cost  = quantity_ordered × unit_cost (null when unit_cost is null)
 *   margin    = (ext_price − ext_cost) / ext_price  (fraction; null when cost unknown or price 0)
 * below_cost is suppressed for override members: their $0.00 is by design, the
 * price lives on the primary.
 */
export function lineFinancials(line) {
  const qty = num(line?.quantity_ordered);
  const unitPrice = num(line?.unit_price);
  const extPrice = qty * unitPrice;
  const unitCost = numOrNull(line?.unit_cost) ?? numOrNull(line?.unit_cost_snapshot);
  const extCost = unitCost == null ? null : qty * unitCost;
  const priceSource = line?.price_source || null;
  const costSource = line?.cost_source || (unitCost != null ? 'snapshot' : 'none');
  let belowCost = typeof line?.below_cost === 'boolean'
    ? line.below_cost
    : (unitCost != null && unitPrice < unitCost - 0.005);
  if (priceSource === 'override_member') belowCost = false;
  // A T&M line waiting on pricing bills $0.00 by design: no below-cost flag, no margin.
  const tmPending = priceSource === 'tm_pending';
  if (tmPending) belowCost = false;
  return {
    qty,
    unit_price: unitPrice,
    ext_price: extPrice,
    unit_cost: unitCost,
    ext_cost: extCost,
    margin: tmPending ? null : marginPct(extCost, extPrice),
    price_source: priceSource,
    cost_source: costSource,
    below_cost: belowCost,
    quote_part_id: line?.quote_part_id ?? null,
    formula_price: `${qty} × ${fmtMoney(unitPrice)} = ${fmtMoney(extPrice)}`,
    formula_cost: unitCost == null ? 'no cost snapshot on this line' : `${qty} × ${fmtMoney(unitCost)} = ${fmtMoney(extCost)}`,
  };
}

/** Sum the flattened lines: subtotal, known cost, how many lines lack a cost. */
export function linesTotals(lines) {
  const fins = (lines || []).filter(Boolean).map(lineFinancials);
  const subtotal = fins.reduce((s, f) => s + f.ext_price, 0);
  const withCost = fins.filter((f) => f.ext_cost != null);
  const cost = withCost.length ? withCost.reduce((s, f) => s + f.ext_cost, 0) : null;
  return {
    line_count: fins.length,
    subtotal,
    cost,
    cost_line_count: withCost.length,
    cost_missing_count: fins.length - withCost.length,
    margin: marginPct(cost, subtotal),
  };
}

/** Find the primary line of an assembly group (the one the ops fold / override lands on). */
export function findPrimaryLine(group) {
  const comps = (group?.components || []).filter(Boolean);
  if (group?.primary_part_id != null) {
    const byPart = comps.find((c) => c.part_id === group.primary_part_id);
    if (byPart) return byPart;
  }
  return comps.find((c) => c.price_source === 'ops_fold' || c.price_source === 'assembly_override') || null;
}

/**
 * Assembly-group money. /truth's group.quantity_ordered is already the SET
 * count (primary qty ÷ ppq — see get_sales_order_source_of_truth), so it is the
 * fallback for `sets`. per_set_price = group_subtotal ÷ sets.
 */
export function groupFinancials(group) {
  const comps = (group?.components || []).filter(Boolean);
  const fins = comps.map(lineFinancials);
  const subtotalLocal = fins.reduce((s, f) => s + f.ext_price, 0);
  const subtotal = numOrNull(group?.group_subtotal) ?? subtotalLocal;
  const setsRaw = numOrNull(group?.sets) ?? numOrNull(group?.quantity_ordered);
  const sets = setsRaw != null && setsRaw > 0 ? setsRaw : null;
  const perSetPrice = numOrNull(group?.per_set_price) ?? (sets ? subtotal / sets : null);
  const withCost = fins.filter((f) => f.ext_cost != null);
  const costLocal = withCost.length ? withCost.reduce((s, f) => s + f.ext_cost, 0) : null;
  const cost = numOrNull(group?.group_cost) ?? costLocal;
  const perSetCost = cost != null && sets ? cost / sets : null;
  const primary = findPrimaryLine(group);
  const fold = primary?.cost_breakdown?.conversion?.fold || null;
  const override = primary?.cost_breakdown?.conversion?.override || null;
  const ppq = numOrNull(group?.primary_part_quantity) ?? numOrNull(override?.ppq) ?? null;
  const opsFoldedPerUnit = numOrNull(group?.ops_folded_per_unit) ?? numOrNull(fold?.per_unit_added);
  const overridePerSet = numOrNull(group?.override_per_set) ?? numOrNull(override?.per_set);
  let pricingNote = group?.pricing_note || null;
  if (!pricingNote) {
    if (overridePerSet != null) {
      const onPrimary = ppq ? overridePerSet / ppq : null;
      pricingNote = `override ${fmtMoney(overridePerSet)}/set`
        + (ppq ? ` ÷ ppq ${ppq} → ${fmtMoney(onPrimary)} on the primary` : '')
        + '; member lines carry $0.00 by design';
    } else if (opsFoldedPerUnit != null) {
      pricingNote = `includes ${fmtMoney(opsFoldedPerUnit)}/ea of assembly operations folded from the quote's ops line`;
    }
  }
  return {
    sets,
    per_set_price: perSetPrice,
    group_subtotal: subtotal,
    group_cost: cost,
    per_set_cost: perSetCost,
    // Recomputed from the sums above (not group_margin_pct) so it is always
    // reproducible from the two figures printed beside it.
    group_margin: marginPct(cost, subtotal),
    cost_missing_count: fins.length - withCost.length,
    ops_folded_per_unit: opsFoldedPerUnit,
    override_per_set: overridePerSet,
    ppq,
    pricing_note: pricingNote,
    primary_line_id: primary?.sales_order_line_id ?? null,
    part_count: comps.filter((c) => !c.consumable_id).length,
    component_count: comps.filter((c) => !!c.consumable_id).length,
  };
}

/**
 * Header totals block. Lines subtotal comes from the enriched header.subtotal,
 * else the truth header's order_total (Σ qty × unit_price). Order Total is the
 * STORED sales_orders.total; `computed` re-derives it so the page can say when
 * the two disagree instead of silently printing one of them.
 */
export function headerTotals(header) {
  const subtotal = numOrNull(header?.subtotal) ?? numOrNull(header?.order_total) ?? 0;
  const shipping = num(header?.shipping_amount);
  const fees = num(header?.additional_fees);
  const discount = num(header?.discount_amount);
  const tax = num(header?.tax_amount);
  const computed = subtotal + shipping + fees + tax - discount;
  const stored = numOrNull(header?.total);
  return {
    subtotal,
    shipping,
    fees,
    discount,
    tax,
    computed,
    stored,
    total: stored ?? computed,
    reconciles: stored == null ? true : approxEq(stored, computed, 0.01),
    formula: 'lines + shipping + fees + tax − discount',
  };
}

export const DEFAULT_COST_BASIS_LABELS = {
  original_cost_estimate: 'Cost at conversion (part rollup)',
  quote_cost_basis: 'Quote cost basis (Σ line snapshots)',
  grand_total_estimated_cost: 'Recalc Est (live rates)',
};

/**
 * Production-summary bases. Est Profit / Est Margin are computed on LINE
 * revenue (order_subtotal — shipping/fees/tax excluded), never on the
 * commitment (= sales_orders.total, which includes pass-through shipping/tax).
 * When the API does not return order_subtotal yet, the truth header's line
 * subtotal is used and `subtotal_source` says so.
 */
export function summaryBases({ summary, header }) {
  const fromSummary = numOrNull(summary?.order_subtotal);
  const orderSubtotal = fromSummary ?? numOrNull(header?.subtotal) ?? numOrNull(header?.order_total);
  const subtotalSource = fromSummary != null ? 'production_summary.order_subtotal' : (orderSubtotal != null ? 'truth header line subtotal' : 'unknown');
  const recalc = numOrNull(summary?.grand_total_estimated_cost);
  const commitment = numOrNull(summary?.original_commitment);
  const estProfit = orderSubtotal == null || recalc == null ? null : orderSubtotal - recalc;
  const estMargin = marginPct(recalc, orderSubtotal);
  const isManual = !header?.quote_id;
  const convRaw = numOrNull(summary?.original_cost_estimate);
  const conversionCost = convRaw != null && convRaw > 0 ? convRaw : null;
  const quoteBasis = numOrNull(summary?.quote_cost_basis) ?? numOrNull(header?.quote_cost_basis);
  const labels = { ...DEFAULT_COST_BASIS_LABELS, ...(summary?.cost_basis_labels || {}) };
  const absentReason = isManual ? 'n/a (manual order)' : 'not recorded';
  const passThrough = commitment != null && orderSubtotal != null ? commitment - orderSubtotal : null;
  return {
    order_subtotal: orderSubtotal,
    subtotal_source: subtotalSource,
    base_label: orderSubtotal == null
      ? 'line revenue unknown'
      : `of ${fmtMoney(orderSubtotal)} line revenue; shipping/fees/tax excluded`,
    commitment,
    pass_through: passThrough,
    recalc_est: recalc,
    est_profit: estProfit,
    est_margin: estMargin,
    quote_cost_basis: quoteBasis,
    conversion_cost: conversionCost,
    is_manual: isManual,
    absent_reason: absentReason,
    labels,
    delta_conversion_vs_quote: quoteBasis != null && conversionCost != null ? conversionCost - quoteBasis : null,
    delta_recalc_vs_conversion: recalc != null && conversionCost != null ? recalc - conversionCost : null,
    delta_recalc_vs_quote: recalc != null && quoteBasis != null ? recalc - quoteBasis : null,
    cost_basis_note: summary?.cost_basis_note
      || 'quote_cost_basis = Σ quote-line cost snapshots at conversion; original_cost_estimate = part-rollup recompute at conversion; Recalc Est = live rates × historical times + current material.',
  };
}

const SPLIT_LABELS = {
  labor: 'Internal labor',
  labor_est: 'Internal labor',
  material: 'Material',
  material_est: 'Material',
  gas_consumables: 'Gas & consumables',
  gas_and_consumables: 'Gas & consumables',
  consumables: 'Gas & consumables',
  coating: 'Coating (outsourced)',
  purchased_components: 'Purchased components',
  purchased_component: 'Purchased components',
  assembly_ops: 'Assembly operations',
  assembly_operations: 'Assembly operations',
  engine_rounding_residual: 'Engine rounding (per-unit cents × qty)',
};

// estimate_splits keys that are NOT components of the total (bench D37): the
// total itself and the raw pre-rounding differences shown beside a component
// (`labor_parts_unrounded`). Summing them made every non-zero estimate read
// "does not sum". engine_rounding_residual IS a component and stays a row.
const isSplitComponentKey = (key) => key !== 'total' && !/_unrounded$/.test(key);

/** estimate_splits {key: amount} -> [{key, label, value}] + total + whether they sum to Recalc Est. */
export function estimateSplitRows(splits, recalcEst) {
  if (!splits || typeof splits !== 'object') return { rows: [], total: null, sums_to_total: null };
  const rows = Object.entries(splits)
    .filter(([key, v]) => isSplitComponentKey(key) && numOrNull(v) != null)
    .map(([key, v]) => ({
      key,
      label: SPLIT_LABELS[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      value: Number(v),
    }));
  const total = rows.reduce((s, r) => s + r.value, 0);
  const rc = numOrNull(recalcEst);
  return { rows, total: rows.length ? total : null, sums_to_total: rc == null || !rows.length ? null : approxEq(total, rc, 0.011) };
}

/** not_yet_realizable_lines -> normalised rows + total amount. */
export function notYetRealizableRows(items) {
  const rows = (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((r) => ({
      so_line_id: r.so_line_id ?? r.sales_order_line_id ?? null,
      product_id: r.product_id || 'N/A',
      amount: num(r.amount),
      reason: r.reason || 'not yet realizable',
    }));
  return { rows, total: rows.reduce((s, r) => s + r.amount, 0) };
}

/**
 * Entry-page line: unit_cost undefined = still loading, null = part has no
 * stored cost, number = parts.cost_labor_material.
 */
export function entryLineFinancials(line) {
  const qty = num(line?.quantity);
  const unitPrice = num(line?.unit_price);
  const costKnown = line?.unit_cost !== undefined;
  const unitCost = costKnown ? numOrNull(line?.unit_cost) : null;
  const ext = qty * unitPrice;
  const extCost = unitCost == null ? null : qty * unitCost;
  return {
    qty,
    unit_price: unitPrice,
    ext_price: ext,
    unit_cost: unitCost,
    cost_loading: !costKnown,
    ext_cost: extCost,
    margin: marginPct(unitCost, unitPrice),
    below_cost: unitCost != null && unitPrice < unitCost - 0.005,
  };
}

/** Tooltip text for the locked unit-price field on the SO Edit Items grid. */
export const LOCKED_PRICE_TOOLTIP =
  'Existing line prices are locked — the API ignores unit_price on existing lines. '
  + 'New lines are priced from the part\'s book price (parts.price, set on quote approval or the Parts page; '
  + 'consumable-only lines from consumables.price) unless you type a price here, which is saved as a manual price. '
  + 'Note: a book price never includes an assembly-ops fold, so a primary added here can bill less than the same part converted from a quote.';

/* ===========================================================================
 * Wave 2 (U-S2): F19 invoices bill the order's per-set price, decision 5a
 * per-line "Recalc Est" from the quote engine, F62 part-vs-assembly coating.
 * Same contract as everything above: consume the enriched keys when present,
 * degrade to the caller's local figure / null (never NaN, never a silent $0)
 * when the API is older or the fetch failed.
 * ======================================================================== */

/** 'SO-1117' from 'SO-1117' | 1117 | ' so1117 '; null when unknown. */
export function soLabel(orderNumber) {
  if (orderNumber === null || orderNumber === undefined) return null;
  const s = String(orderNumber).trim();
  if (!s) return null;
  return `SO-${s.replace(/^so-?/i, '')}`;
}

/** Flatten /truth `lines` entries (assembly_group / item / plain) into line objects. */
export function flattenTruthEntries(entries) {
  const out = [];
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (!entry) return;
    if (entry.type === 'assembly_group') {
      (entry.components || []).forEach((c) => c && out.push(c));
      return;
    }
    if (entry.type === 'item' && entry.data) {
      out.push(entry.data);
      return;
    }
    if (entry.sales_order_line_id != null) out.push(entry);
  });
  return out;
}

/** /truth `lines` → { [source_assembly_id]: group } (assembly_group entries only; string keys). */
export function indexTruthGroups(entries) {
  const out = {};
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    if (entry && entry.type === 'assembly_group' && entry.source_assembly_id != null) {
      out[String(entry.source_assembly_id)] = entry;
    }
  });
  return out;
}

/**
 * F19: an invoice ASSEMBLY line bills the ORDER's per-set price — the figure
 * the quote's Assembly Pricing card and the SO PDF print. Prefer the enriched
 * /truth group (per_set_price, sets — wave 1 C2); the caller's local formula
 * (Σ qty × unit_price ÷ sets over the lines it has) is the fallback for an
 * older API, a failed fetch, or a group that is not in the payload.
 *   per_set_price  truth.per_set_price ?? local
 *   ordered_sets   truth.sets (ORDERED set count) or null — the create flow
 *                  uses it as quantity_ordered; a partial invoice's detail view
 *                  keeps its INVOICED set count and ignores it
 *   drift_vs_local truth − local, so the page can say when the two disagree
 */
export function invoiceAssemblyPricing({ truthGroup, localPerSetPrice, orderNumber } = {}) {
  const truthPerSet = numOrNull(truthGroup?.per_set_price);
  const truthSets = numOrNull(truthGroup?.sets);
  const local = num(localPerSetPrice);
  const fromTruth = truthPerSet != null;
  const label = soLabel(orderNumber) || 'the sales order';
  const drift = fromTruth ? truthPerSet - local : null;
  return {
    per_set_price: fromTruth ? truthPerSet : local,
    ordered_sets: fromTruth ? truthSets : null,
    source: fromTruth ? 'truth' : 'local',
    note: fromTruth
      ? `per-set price from ${label}`
      : 'per-set price computed here (Σ qty × unit price ÷ sets) — order pricing not loaded',
    formula: fromTruth
      ? (truthGroup.pricing_formula || 'per_set_price = Σ(qty × unit_price) ÷ sets; sets = primary qty ÷ ppq')
      : 'Σ(qty × unit_price) ÷ sets',
    pricing_note: (fromTruth && truthGroup.pricing_note) || null,
    local_per_set_price: local,
    drift_vs_local: drift,
    drifts: drift != null && !approxEq(drift, 0, 0.011),
  };
}

const estimateLineId = (r) => r?.so_line_id ?? r?.sales_order_line_id ?? null;

/**
 * A per-unit cost with the digits it actually has (review F10): the API extends
 * the engine's UNROUNDED unit cost, so a $0.0425 fastener must print $0.0425,
 * not $0.04 beside a $212.50 extension. Whole-cent values print as usual.
 */
export function fmtUnitCost(v, { dash = false } = {}) {
  const n = numOrNull(v);
  if (n == null) return fmtMoney(v, { dash });
  const subCent = Math.abs(n * 100 - Math.round(n * 100)) > 1e-6;
  return fmtMoney(n, { precise: subCent });
}

/**
 * estimate_engine_calls → the footer's fallback signal (review F11). Since the
 * API counts only engine ERRORS as `failed`, budget cuts, the part cap, stale
 * and $0 material each need their own words or a slow day reads "0 failed".
 */
export function engineCallsSummary(calls) {
  if (!calls || typeof calls !== 'object') return [];
  const n = (k) => Number(calls[k]) || 0;
  const out = [];
  if (calls.engine_skipped_for_budget) out.push('engine skipped (request budget exhausted)');
  if (n('failed')) out.push(`${n('failed')} failed`);
  if (n('over_budget')) out.push(`${n('over_budget')} past the time budget`);
  if (n('capped')) out.push(`${n('capped')} capped (large order)`);
  if (n('stale_material')) out.push(`${n('stale_material')} stale material`);
  if (n('zero_material')) out.push(`${n('zero_material')} with $0 computed material (rollup used)`);
  if (n('zero_material_kept')) out.push(`${n('zero_material_kept')} with $0 material`);
  return out;
}
// An 'assembly_ops' estimate reuses its PRIMARY line's so_line_id (bench D08), so
// a line id alone cannot tell the two rows apart: key ops rows by assembly too.
const estimateRowKey = (r, id) => (r && typeof r === 'object' && r.kind === 'assembly_ops'
  ? `ops:${id ?? ''}:${r.assembly_id ?? ''}`
  : `line:${id ?? ''}`);
const fmtQty = (q) => (q == null ? '?' : (Number.isInteger(q) ? String(q) : q.toFixed(2)));

/**
 * Decision 5a: production_summary.line_estimates — one row per SO line priced
 * by the quote engine at today's inputs ({so_line_id, product_id, unit_cost,
 * ext_cost, basis, error?}). qty comes from the row when the API sends one,
 * else from the matching /truth line (quantity_ordered), else ext ÷ unit, so
 * every row can print "unit × qty = ext". estimate_fallback_lines (ids or
 * objects) names the lines where the engine failed and the old per-line
 * arithmetic was used; a fallback id that has no line_estimates row becomes a
 * `fallback_only_rows` entry. `remainder` = Recalc Est − Σ line ext.
 *
 * Assembly-operations rows (kind 'assembly_ops') are per SET: their qty is the
 * row's own quantity / sets, never the primary line's /truth quantity (sets ×
 * ppq), and they are told apart from the primary by assembly_id (bench D08).
 * The remainder is explained from what the API says it is (bench D17): the
 * assembly consumables (estimate_splits.assembly_consumables, no line entry)
 * plus the per-line cent rounding (line_estimates_rounding) — ops rows are
 * already lines. `remainder_parts` lists them, `remainder_unexplained` is what
 * is left (null when the API sends neither figure).
 */
export function lineEstimateRows(lineEstimates, fallbackLines, truthEntries,
  { recalcEst, assemblyConsumables, lineRounding } = {}) {
  const byId = {};
  flattenTruthEntries(truthEntries).forEach((l) => {
    if (l?.sales_order_line_id != null) byId[String(l.sales_order_line_id)] = l;
  });
  const fallbackIds = new Set();
  const fallbackRows = (Array.isArray(fallbackLines) ? fallbackLines : [])
    .filter((f) => f !== null && f !== undefined)
    .map((f) => {
      const isObj = typeof f === 'object';
      const id = isObj ? estimateLineId(f) : f;
      const idKey = id != null ? String(id) : null;
      if (idKey) fallbackIds.add(estimateRowKey(isObj ? f : null, idKey));
      const tl = idKey ? byId[idKey] : null;
      return {
        so_line_id: id ?? null,
        row_key: estimateRowKey(isObj ? f : null, idKey),
        product_id: (isObj && f.product_id) || tl?.product_id || (idKey ? `line #${idKey}` : 'unknown line'),
        reason: (isObj && (f.error || f.reason)) || 'quote engine unavailable — old per-line arithmetic used',
      };
    });
  const rows = (Array.isArray(lineEstimates) ? lineEstimates : []).filter(Boolean).map((r) => {
    const id = estimateLineId(r);
    const idKey = id != null ? String(id) : null;
    const tl = idKey ? byId[idKey] : null;
    const unit = numOrNull(r.unit_cost);
    const ext = numOrNull(r.ext_cost);
    const isOps = r.kind === 'assembly_ops';
    const rowKey = estimateRowKey(r, idKey);
    let qty = numOrNull(r.quantity) ?? numOrNull(r.qty) ?? numOrNull(r.quantity_ordered)
      ?? (isOps ? numOrNull(r.sets) : numOrNull(tl?.quantity_ordered));
    if (qty == null && unit != null && unit !== 0 && ext != null) qty = ext / unit;
    const extResolved = ext ?? (unit != null && qty != null ? unit * qty : null);
    const isFallback = (idKey != null && fallbackIds.has(rowKey)) || /fallback/i.test(String(r.basis || ''));
    return {
      so_line_id: id ?? null,
      kind: r.kind || null,
      assembly_id: r.assembly_id ?? null,
      row_key: `${rowKey}:${r.product_id ?? ''}`,
      product_id: r.product_id || tl?.product_id || (idKey ? `line #${idKey}` : 'line'),
      qty,
      unit_cost: unit,
      ext_cost: extResolved,
      basis: r.basis || (isFallback ? 'fallback: old per-line arithmetic' : 'quote engine at today\'s inputs'),
      error: r.error || null,
      fallback: isFallback,
      formula: unit == null
        ? 'unit cost unknown'
        : `${fmtUnitCost(unit)} × ${fmtQty(qty)} = ${fmtMoney(extResolved)}`,
      // unit_cost is rounded to 4 dp and ext_cost to 2 dp upstream, so the
      // tolerance grows with qty (0.00005 × qty of rounding + a cent).
      sums_ok: ext != null && unit != null && qty != null
        ? approxEq(unit * qty, ext, 0.011 + Math.abs(qty) * 0.0001)
        : null,
    };
  });
  const rowKeys = new Set(rows.filter((r) => r.so_line_id != null)
    .map((r) => estimateRowKey(r, String(r.so_line_id))));
  const total = rows.reduce((s, r) => s + (r.ext_cost ?? 0), 0);
  const rc = numOrNull(recalcEst);
  const remainder = rc == null || !rows.length ? null : rc - total;
  const asmCons = numOrNull(assemblyConsumables);
  const rounding = numOrNull(lineRounding);
  const remainderParts = [];
  if (asmCons != null && !approxEq(asmCons, 0, 0.005)) remainderParts.push({ label: 'assembly consumables', value: asmCons });
  if (rounding != null && !approxEq(rounding, 0, 0.005)) remainderParts.push({ label: 'per-line cent rounding', value: rounding });
  const explained = asmCons == null && rounding == null ? null : (asmCons ?? 0) + (rounding ?? 0);
  const unexplained = remainder == null || explained == null ? null : remainder - explained;
  let remainderLabel = 'components not priced per line (assembly consumables)';
  if (explained != null) {
    const parts = remainderParts.map((p) => `${p.label} ${fmtMoney(p.value)}`);
    if (unexplained != null && !approxEq(unexplained, 0, 0.011)) parts.push(`unexplained ${fmtMoney(unexplained)}`);
    remainderLabel = parts.length ? parts.join(' + ') : 'nothing outside the lines';
  }
  return {
    rows,
    count: rows.length,
    engine_count: rows.filter((r) => !r.fallback).length,
    total: rows.length ? total : null,
    remainder,
    remainder_is_zero: remainder == null ? null : approxEq(remainder, 0, 0.011),
    remainder_parts: remainderParts,
    remainder_unexplained: unexplained,
    remainder_label: remainderLabel,
    fallback_rows: fallbackRows,
    fallback_only_rows: fallbackRows.filter((f) => f.so_line_id == null || !rowKeys.has(f.row_key)),
    fallback_count: fallbackRows.length,
  };
}

const COATING_RE = /coating/i;
const OUTSOURCED_RE = /outsourc/i;

/** The API's own "Coating (outsourced)" station row (C2.3) — the page renders that row itself. */
export function isOutsourcedCoatingRow(station) {
  const ws = String(station?.workstation || '');
  return COATING_RE.test(ws) && OUTSOURCED_RE.test(ws);
}

/** Timed station rows for the table: the API's outsourced-coating row is rendered separately. */
export function stationRowsForTable(stations) {
  return (Array.isArray(stations) ? stations : []).filter((s) => s && !isOutsourcedCoatingRow(s));
}

/**
 * F62: "Part coating $X · Assembly coating $Y" for the Coating (outsourced)
 * row. Reads est_part_coating / est_assembly_coating from a coating station
 * row when the API sends them (C2.3), else estimate_splits.coating_parts /
 * coating_assembly_ops (wave 1), else nothing (source null). The two are the
 * components of the row's Est, so `sums_to_est` says whether they add up.
 */
export function coatingRowSplit({ stations, splits, grandTotalCoatingEst } = {}) {
  const row = (Array.isArray(stations) ? stations : []).find((s) => s
    && COATING_RE.test(String(s.workstation || ''))
    && (numOrNull(s.est_part_coating) != null || numOrNull(s.est_assembly_coating) != null)) || null;
  let part = null;
  let assembly = null;
  let source = null;
  if (row) {
    part = numOrNull(row.est_part_coating);
    assembly = numOrNull(row.est_assembly_coating);
    source = 'station_row';
  } else if (splits && (numOrNull(splits.coating_parts) != null || numOrNull(splits.coating_assembly_ops) != null)) {
    part = numOrNull(splits.coating_parts);
    assembly = numOrNull(splits.coating_assembly_ops);
    source = 'estimate_splits';
  }
  const total = source ? (part ?? 0) + (assembly ?? 0) : null;
  const est = numOrNull(grandTotalCoatingEst);
  const sourceLabels = {
    station_row: 'from the station row (est_part_coating / est_assembly_coating)',
    estimate_splits: 'from estimate_splits (coating_parts / coating_assembly_ops)',
  };
  return {
    part,
    assembly,
    source,
    total,
    station_row: row,
    sums_to_est: total == null || est == null ? null : approxEq(total, est, 0.011),
    text: source ? `Part coating ${fmtMoney(part ?? 0)} · Assembly coating ${fmtMoney(assembly ?? 0)}` : null,
    source_label: sourceLabels[source] || 'not returned by this API version',
  };
}

/**
 * The alert after DELETE /sales_orders/{id}. The API's message is a full sentence ("Sales Order
 * SO-1301 permanently deleted." plus " Quote #501 is back to Approved." when the quote was
 * restored); the removed draft jobs follow as a sentence of their own.
 */
export function soDeletedAlertText(soNum, body) {
  const b = body || {};
  const head = b.message || `${soNum} permanently deleted.`;
  return `${head}${b.jobs_deleted ? ` ${b.jobs_deleted} draft job(s) removed.` : ''}`;
}

/** The Production Financial Summary's accrued money (what the floor has spent so far against what
 *  is billable now): outsourced finishing is the invoiced actual when there is one, otherwise the
 *  committed order value (never both); accrued cost = labour + consumables + material + finishing;
 *  margin is a PERCENT of the realizable revenue (0 when there is none). The desktop Sales Order
 *  page and the phone order screen both read it. */
export function accruedFinancials(summary) {
  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };
  const s = summary || {};
  const finishingCostToDate = n(s.grand_total_finishing_cost_to_date) > 0
    ? n(s.grand_total_finishing_cost_to_date)
    : Math.max(n(s.grand_total_finishing_cost_act), n(s.grand_total_finishing_cost_est));
  const finishingIsInvoiced = n(s.grand_total_finishing_cost_act) > 0;
  const actualTotalCost = n(s.grand_total_labor_cost)
    + n(s.grand_total_consumable_cost_act)
    + n(s.grand_total_material_cost)
    + finishingCostToDate;
  const revenue = s.actual_realizable_revenue;
  const actualProfit = revenue - actualTotalCost;
  const actualMargin = revenue > 0 ? (actualProfit / revenue) * 100 : 0;
  return { finishingCostToDate, finishingIsInvoiced, actualTotalCost, actualProfit, actualMargin };
}
